import { Component, effect, inject, input, OnDestroy } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import { BsModalRef } from 'ngx-bootstrap/modal';
import { combineLatestWith, Observable, Subject, Subscription } from 'rxjs';

import { UserDetailsStateService } from '@bn/auth/services';
import { UserStateService } from '@bn/auth/stores';
import { APP_CONFIG, ClickActionEvent } from '@bn/primitives';
import { ContentService } from '@bn/shared/core/services';
import { ModalState } from '@bn/web-ui/dialog';

@Component({
  templateUrl: './extend-trial-modal.component.html',
  styleUrls: ['./extend-trial-modal.component.scss'],
  standalone: false,
})
export class ExtendTrialModalComponent implements OnDestroy {
  readonly APP_CONFIG = inject(APP_CONFIG);
  readonly #accountStore = inject(UserStateService).accountStore;
  readonly #contentService = inject(ContentService);
  readonly #dialog = inject<BsModalRef<ExtendTrialModalComponent>>(BsModalRef);
  readonly #route = inject(ActivatedRoute);
  readonly #subscriptions = new Subscription();
  readonly #userDetailsService = inject(UserDetailsStateService);
  readonly #userStore = inject(UserStateService).userStore;
  readonly #onAction = new Subject<ClickActionEvent>();

  public trialBalance = 0;
  public usedTrialBalance = 0;
  public totalTrialBalance = 0;
  public data?: ModalState;
  public isLoading = false;
  public isSuccess = false;
  public isError = false;
  public isAutoExtend = false;
  public FreeTrialExtendedDate: string | null = null;
  public totalBandwidth = 0;
  public usedBandwidth = 0;

  /**
   * Indicates whether the container size should be reset to 'lg' when the directive is destroyed.
   * Set to `true` to reset the container size; set to `false` to avoid resetting it.
   *
   * @example
   * <div [resetContainerSizeOnDestroy]="false"></div> <!-- Will not reset container size -->
   *
   * @default true
   */
  resetContainerSizeOnDestroy = input(true);

  constructor() {
    toObservable(this.#accountStore.billingTrialOverview)
      .pipe(takeUntilDestroyed(), combineLatestWith(this.#route.fragment))
      .subscribe(([billingTrialOverview]) => {
        if (!billingTrialOverview) return;

        this.trialBalance = billingTrialOverview.TrialBalance;
        this.usedTrialBalance = billingTrialOverview.TrialUsage;
        this.totalTrialBalance = this.trialBalance + this.usedTrialBalance;
      });

    effect(() => {
      const user = this.#userStore.userDetails();
      if (user) {
        this.isAutoExtend = !!this.data?.extra?.isAuto;

        this.FreeTrialExtendedDate = user.FreeTrialExtendedDate ?? null;
        this.totalBandwidth = user.TrialBandwidthLimit ?? 0;
        this.usedBandwidth = user.TotalBandwidthUsed ?? 0;

        if (!this.FreeTrialExtendedDate && this.isAutoExtend) {
          this.onExtendTrialClick();
        }
      }
    });
  }

  public ngOnDestroy(): void {
    this.#subscriptions.unsubscribe();

    if (this.resetContainerSizeOnDestroy()) this.#contentService.setContainerSize(); // Empty for default = 'lg'
  }

  public onExtendTrialClick(): void {
    this.isLoading = true;

    this.#userDetailsService.extendFreeTrial().subscribe({
      next: () => {
        this.isSuccess = true;
        this.isLoading = false;

        this.#onAction.next({
          action: 'confirm',
        });
      },
      error: () => {
        this.isError = true;
        this.isLoading = false;

        if (this.isAutoExtend) {
          this.isAutoExtend = false;
          this.isError = false;
        }
      },
    });
  }

  public getOnAction$(): Observable<ClickActionEvent> {
    return this.#onAction.asObservable();
  }

  public onClose(): void {
    this.#dialog.hide();
  }
}
